import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface BlockedWalletAsset {
	symbol: string;
	balance: string;
	tokenAddress?: string;
	isNative: boolean;
}

export interface BlockedWallet {
	id: number;
	address: string;
	blockchain: string;
	blockReason: string;
	blockedAt: string;
	assets: BlockedWalletAsset[];
}

export interface FundReturn {
	id: number;
	walletId: number;
	walletAddress: string;
	blockchain: string;
	networkId: string;
	destinationAddress: string;
	amount: string;
	currency: string;
	usdAmount: string;
	status: "pending" | "inProgress" | "completed" | "failed";
	transactionHash: string | null;
	reason: string;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
}

export interface CreateFundReturnRequest {
	walletId: number;
	destinationAddress: string;
	amount: string;
	currency: string; // Symbol like "USDT", "ETH", etc.
	tokenAddress?: string; // Required for tokens, omit for native coins
	reason: string;
	totpCode: string;
}

export interface BroadcastFundReturnRequest {
	totpCode: string;
}

interface BackendBlockedWalletAsset {
	symbol: string;
	balance: string;
	tokenAddress?: string;
	isNative: boolean;
}

interface BackendBlockedWallet {
	id: number;
	address: string;
	blockchain: string;
	blockReason: string;
	blockedAt: string;
	assets: BackendBlockedWalletAsset[];
}

interface BackendAdminInfo {
	id: number;
	email: string;
}

interface BackendFundReturn {
	id: number;
	walletId: number;
	walletAddress: string;
	blockchain: string;
	destinationAddress: string;
	amount: string;
	currency: string;
	fee: string;
	usdAmount: string;
	status: string;
	transactionHash?: string | null;
	reason: string;
	createdBy: BackendAdminInfo;
	createdAt: string;
}

interface ListBlockedWalletsResponse {
	wallets: BackendBlockedWallet[];
}

// Backend returns array directly, not wrapped
type ListFundReturnsResponse = BackendFundReturn[];

// Backend returns object directly, not wrapped
type FundReturnResponse = BackendFundReturn;

function transformBlockedWallet(w: BackendBlockedWallet): BlockedWallet {
	return {
		id: w.id,
		address: w.address,
		blockchain: w.blockchain,
		blockReason: w.blockReason,
		blockedAt: w.blockedAt,
		assets: w.assets.map((a) => ({
			symbol: a.symbol,
			balance: a.balance,
			tokenAddress: a.tokenAddress,
			isNative: a.isNative,
		})),
	};
}

function transformFundReturn(fr: BackendFundReturn): FundReturn {
	return {
		id: fr.id,
		walletId: fr.walletId,
		walletAddress: fr.walletAddress,
		blockchain: fr.blockchain,
		networkId: fr.blockchain, // Use blockchain as networkId since backend doesn't return it
		destinationAddress: fr.destinationAddress,
		amount: fr.amount,
		currency: fr.currency,
		usdAmount: fr.usdAmount,
		status: fr.status as FundReturn["status"],
		transactionHash: fr.transactionHash ?? null,
		reason: fr.reason,
		createdBy: fr.createdBy.email,
		createdAt: fr.createdAt,
		updatedAt: fr.createdAt, // Backend doesn't return updatedAt
	};
}

const fundReturnProvider = {
	async listBlockedWallets(): Promise<BlockedWallet[]> {
		const response = await apiRequest.get<ListBlockedWalletsResponse>(
			withApiPath("/wallets/blocked"),
		);
		return response.data.wallets.map(transformBlockedWallet);
	},

	async listFundReturns(
		params: { limit?: number; offset?: number; status?: string } = {},
	): Promise<{ fundReturns: FundReturn[]; total: number }> {
		const response = await apiRequest.get<ListFundReturnsResponse>(
			withApiPath("/fund-returns"),
			{
				params: {
					limit: params.limit ?? 50,
					offset: params.offset ?? 0,
					status: params.status ?? "",
				},
			},
		);
		const fundReturns = response.data.map(transformFundReturn);
		return {
			fundReturns,
			total: fundReturns.length,
		};
	},

	async getFundReturn(id: number): Promise<FundReturn> {
		const response = await apiRequest.get<FundReturnResponse>(
			withApiPath(`/fund-returns/${id}`),
		);
		return transformFundReturn(response.data);
	},

	async createFundReturn(params: CreateFundReturnRequest): Promise<FundReturn> {
		const body: Record<string, unknown> = {
			walletId: params.walletId,
			destinationAddress: params.destinationAddress,
			amount: params.amount,
			currency: params.currency,
			reason: params.reason,
		};
		if (params.tokenAddress) {
			body.tokenAddress = params.tokenAddress;
		}
		const response = await apiRequest.post<FundReturnResponse>(
			withApiPath("/fund-returns"),
			body,
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return transformFundReturn(response.data);
	},

	async broadcastFundReturn(
		id: number,
		params: BroadcastFundReturnRequest,
	): Promise<FundReturn> {
		const response = await apiRequest.post<FundReturnResponse>(
			withApiPath(`/fund-returns/${id}/broadcast`),
			{},
			{
				headers: {
					"X-2FA-Code": params.totpCode,
				},
			},
		);
		return transformFundReturn(response.data);
	},
};

export default fundReturnProvider;
